import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, Home, Info, Clock, DollarSign, MessageSquareText, Send } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    // If not on home page, first navigate to home
    if (window.location.pathname !== '/') {
      navigate('/');
    }

    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const navItems = [
    { name: "Home", path: "/", icon: Home },
    { name: "About", path: "/about", icon: Info },
    { name: "How We Work", path: "/how-we-work", icon: Clock },
    { 
      name: "Plans", 
      onClick: () => scrollToSection("pricing"), 
      icon: DollarSign 
    },
    { 
      name: "Testimonials", 
      onClick: () => scrollToSection("testimonials"), 
      icon: MessageSquareText 
    },
    { name: "Contact", path: "/contact", icon: Send },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {/* Company Info */}
          <div className="flex flex-col items-start">
            <Link to="/" className="mb-4">
              <img
                src="/images/logo.png"
                alt="Aditon Advertising Logo"
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-sm leading-relaxed mb-4 max-w-md">
              Empowering immigration professionals with qualified HNWI leads and innovative marketing solutions.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/share/1DhY18nnnV/?mibextid=LQQJ4d" className="hover:text-blue-400 transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/aditonadvertising" className="hover:text-blue-400 transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="https://www.linkedin.com/company/aditon-advertising/" className="hover:text-blue-400 transition-colors" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <div className="grid grid-cols-2 gap-4">
              {navItems.map((item) => (
                item.path ? (
                  <Link 
                    key={item.name} 
                    to={item.path} 
                    className="flex items-center hover:text-blue-400 transition-colors"
                  >
                    <item.icon size={16} className="mr-2" />
                    {item.name}
                  </Link>
                ) : (
                  <button 
                    key={item.name} 
                    onClick={item.onClick}
                    className="flex items-center hover:text-blue-400 transition-colors text-left"
                  >
                    <item.icon size={16} className="mr-2" />
                    {item.name}
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="md:col-span-2 lg:col-span-1">
            <h3 className="text-lg font-semibold text-white mb-4">Contact Us</h3>
            <div className="space-y-3">
              <p className="flex items-center">
                <Mail size={18} className="mr-2 flex-shrink-0" />
                <a href="mailto:info@aditonadvertising.com" className="hover:text-blue-400 transition-colors">
                  info@aditonadvertising.com
                </a>
              </p>
              <p className="flex items-center">
                <Phone size={18} className="mr-2 flex-shrink-0" />
                <a href="tel:+917410893130" className="hover:text-blue-400 transition-colors">
                +91 74108 93130
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm mb-4 md:mb-0 text-gray-400">
              © {currentYear} Aditon Advertising. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy-policy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-blue-400 transition-colors">Terms of Service</Link>
              <Link to="/cookie-policy" className="hover:text-blue-400 transition-colors">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;